import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import feedbackLottie from "../../assets/animations/feedback.lottie";
import "./FeedbackLoadingOverlay.css";

const DEFAULT_MESSAGES = [
  "Preparing your results",
  "Reviewing your speech patterns and pacing",
  "Hang tight - this will just take a moment",
];

export default function FeedbackLoadingOverlay({
  open = true,
  text = "",
  backdropOpacity = 0.35,
}) {
  const [root, setRoot] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const messages = useMemo(() => {
    const primary = typeof text === "string" ? text.trim() : "";
    if (!primary) return DEFAULT_MESSAGES;
    return [primary, ...DEFAULT_MESSAGES.filter((message) => message !== primary)];
  }, [text]);

  useLayoutEffect(() => {
    let el = document.getElementById("overlay-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "overlay-root";
      document.body.appendChild(el);
    }
    setRoot(el);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % messages.length);
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [messages.length, open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [messages]);

  if (!open || !root) return null;

  return createPortal(
    <div
      className="feedback-loading-overlay"
      role="status"
      aria-live="polite"
      style={{ "--feedback-overlay-opacity": backdropOpacity }}
    >
      <div className="feedback-loading-card">
        <DotLottieReact
          src={feedbackLottie}
          autoplay
          loop
          className="feedback-loading-lottie"
          onError={(e) => console.warn("Feedback lottie failed to load", e)}
        />

        <div className="feedback-loading-copy" aria-label={messages[activeIndex]}>
          <div className="feedback-loading-copy-track">
            <span key={messages[activeIndex]} className="feedback-loading-copy-line">
              {messages[activeIndex]}
            </span>
          </div>
        </div>
      </div>
    </div>,
    root
  );
}
